import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Label, Spinner } from "reactstrap";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { ProjectButton } from "./style";
import { importProject } from "./service";
import { useParams } from "react-router-dom";

const ProjectImport = ({ setCurrentTab, loadProject }) => {
  const params = useParams();
  const uploadFileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clearProject, setClearProject] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disableUpload, setDisableUpload] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [customErrors, setCustomErrors] = useState([]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  useEffect(() => {
    setDisableUpload(selectedFile ? false : true);
  }, [selectedFile]);

  const uploadCsv = (canClearProject, file) => {
    setActionLoading(true);
    setCustomErrors([]);

    importProject({ canClearProject, file, projectId: params.projectId })
      .then(({ message }) => {
        setIsNotificationVisible(true);
        setNotification({ message: message, type: "success" });
        loadProject({ fromImportProject: true });
        setCurrentTab("info");
      })
      .catch(({ error }) => {
        if (error.status == 406)
          setCustomErrors(error.errors);
        else {
          setIsNotificationVisible(true);
          setNotification({ message: error, type: "error" });
        }
      })
      .finally(() => setActionLoading(false));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;

      if (fileType !== "text/csv") {
        setErrorMessage("Please select a valid image file.");
        setSelectedFile(null);
        event.target.value = "";
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const removeFile = () => {
    uploadFileInputRef.current.value = "";
    setSelectedFile(null);
  };

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {actionLoading && (
        <CustomizationSpinnerContainer top="200px">
          <Spinner />
        </CustomizationSpinnerContainer>
      )}
      <div className={`${actionLoading ? "blurred" : ""}`}>
        <div className="d-flex justify-content-between align-items-center border-bottom p-3">
          <p className="fw-bold fs-6 m-0">Import Project</p>
          <i
            onClick={() => setCurrentTab("info")}
            className="fa-solid fa-xmark fs-4 pointer text-secondary"
          />
        </div>
        {customErrors.length > 0 && (
          <div className="position-relative px-3">
            <div
              className="rounded-2 bg-white mt-3"
              style={{ border: "1px solid #dddddd" }}
            >
              <div
                className="py-2 px-3 fs-14px"
                style={{ borderBottom: "1px solid #dddddd", color: "#F11F1F" }}
              >
                <i className="fa-solid fa-circle-exclamation me-2"></i>
                Please fix the following errors
              </div>
              {customErrors.map((e, index) => (
                <div
                  key={index}
                  className="py-2 px-3"
                  style={
                    customErrors.length - 1 != index
                      ? {
                          borderBottom: "1px solid #dddddd",
                        }
                      : {}
                  }
                >
                  {`${e.s_no ? `[S.No.${e.s_no}]` : ""} ${e.messages.reduce(
                    (acc, value) => acc.concat(value),
                    ""
                  )}`}
                </div>
              ))}
            </div>
            <div
              onClick={() => setCustomErrors([])}
              className="position-absolute d-flex bg-white rounded-circle pointer z-2"
              style={{
                top: "-0.4rem",
                right: "0.5rem",
                border: "1px solid #283039",
                padding: "3px 5px 3px 5px",
              }}
            >
              <i className="fa-solid fa-xmark fs-" />
            </div>
          </div>
        )}
        <div>
          <div className="p-3 border-bottom">
            <p className="mb-1 text-secondary">CSV</p>
            <div className="d-flex align-items-center flex-wrap">
              <Label
                className="border border-secondary rounded-2 m-0"
                htmlFor="file-upload"
                style={{ padding: "6px 14px" }}
              >
                Select File
              </Label>
              <Input
                className="d-none"
                id="file-upload"
                type="file"
                accept="text/csv"
                onChange={handleFileChange}
                innerRef={uploadFileInputRef}
              />

              {selectedFile && (
                <div
                  className="d-flex align-items-center ms-2 column-gap-1"
                  style={{ fontSize: "14px" }}
                >
                  <span>{selectedFile.name}</span>
                  <i
                    className="fa-regular fa-circle-xmark pointer danger-on-hover"
                    onClick={() => removeFile()}
                  />
                </div>
              )}

              {errorMessage && (
                <span className="ms-2 text-danger">{errorMessage}</span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center column-gap-2 p-3 border-bottom">
            <Input
              type="checkbox"
              id="clear-project"
              className="m-0"
              checked={clearProject}
              onChange={(e) => setClearProject(!clearProject)}
            />
            <Label for="clear-project" className="m-0 text-secondary">
              Clear Project
            </Label>
          </div>
          <div className="d-flex p-3 border-bottom column-gap-3">
            <ProjectButton
              onClick={() => uploadCsv(clearProject, selectedFile)}
              disabled={disableUpload}
              className="rounded-1"
            >
              Upload
            </ProjectButton>
            <Button
              onClick={() => setCurrentTab("info")}
              className="rounded-1"
              outline
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectImport;
